import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, ShieldCheck, Wallet, Coins, Clock, AlertCircle, CheckCircle2 } from "lucide-react";

/**
 * Beginner-friendly customer guide for paying with crypto. Updated to reflect
 * Helixora's actual accepted coins + networks:
 *   - USDC on Solana
 *   - USDT on TRC20 (TRON)
 *   - BTC on Bitcoin
 *   - ETH on Ethereum (ERC-20)
 *   - XRP on XRP Ledger
 *
 * Linked from: Checkout (payment block), Home (payment section + FAQ), Footer.
 */
export default function HowToPayWithCrypto() {
    const accepted = [
        { sym: "USDC", net: "Solana", note: "Recommended for beginners. Cheapest fees. 1-minute settle.", color: "text-neon", border: "border-neon/40" },
        { sym: "USDT", net: "TRON (TRC20)", note: "Cheapest dollar-pegged transfer. ~2 minute settle.", color: "text-neon", border: "border-neon/40" },
        { sym: "BTC", net: "Bitcoin", note: "Universally available. 10 to 60 min settle.", color: "text-gold", border: "border-gold/40" },
        { sym: "ETH", net: "Ethereum (ERC-20)", note: "Mainnet ETH only. 1 to 5 min settle. Higher fees.", color: "text-gold", border: "border-gold/40" },
        { sym: "XRP", net: "XRP Ledger", note: "Very fast (~5 sec settle). Low fees.", color: "text-gold", border: "border-gold/40" },
    ];

    return (
        <div className="bg-ink-base text-ink min-h-screen">
            <div className="max-w-3xl mx-auto px-6 sm:px-8 py-16">
                <p className="overline text-neon mb-3">/ PAYMENT · CUSTOMER GUIDE</p>
                <h1 className="font-display text-4xl sm:text-6xl font-bold tracking-tight leading-[0.92] mb-6">
                    How to pay<br /><span className="text-neon">with crypto.</span>
                </h1>
                <p className="text-lg text-ink-dim leading-relaxed max-w-2xl">
                    Brand new to cryptocurrency? Don&apos;t worry. You don&apos;t need to understand blockchains
                    or own a hardware wallet. With <strong className="text-ink">USDC</strong> (a stablecoin pegged 1:1 to the US dollar),
                    paying with crypto is just like sending an online transfer. This guide takes about <strong className="text-ink">3 minutes</strong>.
                </p>

                {/* TL;DR */}
                <div className="mt-10 p-6 border border-neon/40 bg-neon/5" data-testid="tldr-block">
                    <p className="overline text-neon mb-2">TL;DR &middot; the 60-second version</p>
                    <ol className="space-y-2 text-sm text-ink-dim list-decimal pl-5">
                        <li>Sign up for a regulated exchange like <strong className="text-ink">Coinbase</strong>, <strong className="text-ink">Kraken</strong>, or <strong className="text-ink">Crypto.com</strong>.</li>
                        <li>Buy <strong className="text-ink">USDC on Solana</strong> (US dollar stablecoin, cheapest network for beginners). $300 of USDC = $300, no exchange-rate surprises.</li>
                        <li>Add our order amount to your cart, click <strong className="text-ink">Pay with Crypto</strong>. You&apos;ll see a wallet address.</li>
                        <li>In your exchange app, hit <strong className="text-ink">Send</strong>, paste the address, choose the matching network (<strong className="text-ink">Solana</strong> in this example), send the exact amount.</li>
                        <li>Network confirms in 1 to 5 minutes. We auto-confirm your order. Tracking arrives once we ship.</li>
                    </ol>
                </div>

                {/* Accepted coins */}
                <section className="mt-16" data-testid="accepted-coins">
                    <p className="overline text-gold mb-3">/ ACCEPTED COINS</p>
                    <h2 className="font-display text-3xl font-bold text-ink leading-tight mb-4 flex items-center gap-3">
                        <Coins className="w-7 h-7 text-gold" /> The 5 we accept.
                    </h2>
                    <p className="text-base text-ink-dim leading-relaxed mb-5">
                        Each coin <strong className="text-ink">must be sent on the matching network</strong>. If your exchange offers
                        a choice (USDC has Solana / Ethereum / Polygon, USDT has TRC20 / ERC-20 / etc.), pick the one shown on our invoice.
                        Sending on the wrong network = lost funds.
                    </p>
                    <div className="grid sm:grid-cols-2 gap-3">
                        {accepted.map((c) => (
                            <div key={c.sym} className={`p-4 border ${c.border} bg-ink-surface`}>
                                <div className="flex items-baseline justify-between gap-3">
                                    <span className={`font-display text-2xl font-bold ${c.color}`}>{c.sym}</span>
                                    <span className="overline text-[10px] text-ink-mute">{c.net}</span>
                                </div>
                                <p className="text-[11px] text-ink-dim mt-2 leading-snug">{c.note}</p>
                            </div>
                        ))}
                    </div>
                </section>

                {/* Why crypto */}
                <section className="mt-16">
                    <p className="overline text-gold mb-3">/ 01 &middot; WHY CRYPTO</p>
                    <h2 className="font-display text-3xl font-bold text-ink leading-tight mb-4">Why we accept crypto only.</h2>
                    <p className="text-base text-ink-dim leading-relaxed">
                        Research peptides are a legal grey area for most card processors. Card networks like Visa, Mastercard,
                        and Stripe can decline or freeze merchant accounts for selling research-grade compounds, even when
                        every product is third-party-tested and labelled &laquo;for research use only&raquo;.
                    </p>
                    <p className="text-base text-ink-dim leading-relaxed mt-3">
                        Crypto is permissionless: no card network in the middle, no risk of declined transactions
                        or sudden processor bans. The tradeoff: you spend 3 minutes setting up an exchange account once,
                        and from then on every order takes 30 seconds to pay.
                    </p>
                </section>

                {/* Step 1 */}
                <section className="mt-16">
                    <p className="overline text-gold mb-3">/ 02 &middot; SET UP AN EXCHANGE</p>
                    <h2 className="font-display text-3xl font-bold text-ink leading-tight mb-4 flex items-center gap-3">
                        <Wallet className="w-7 h-7 text-neon" /> Pick a beginner-friendly exchange.
                    </h2>
                    <p className="text-base text-ink-dim leading-relaxed">
                        These are the most popular regulated exchanges. Any of them works. Pick the one that&apos;s
                        available in your country.
                    </p>
                    <div className="mt-5 grid sm:grid-cols-3 gap-3">
                        {[
                            { name: "Coinbase", url: "https://www.coinbase.com/", note: "US · CA · UK · EU · most countries", good: "Cleanest beginner UI" },
                            { name: "Kraken", url: "https://www.kraken.com/", note: "US · CA · UK · EU · global", good: "Lower fees" },
                            { name: "Crypto.com", url: "https://crypto.com/", note: "180+ countries", good: "Has a debit card too" },
                        ].map((x) => (
                            <a key={x.name} href={x.url} target="_blank" rel="noopener noreferrer"
                                className="p-4 border border-ink-strong hover:border-neon bg-ink-surface transition-colors group">
                                <div className="flex items-center justify-between">
                                    <span className="font-display text-lg font-bold text-ink">{x.name}</span>
                                    <ArrowUpRight className="w-4 h-4 text-ink-mute group-hover:text-neon transition-colors" />
                                </div>
                                <p className="text-[10px] uppercase font-mono tracking-wider text-ink-mute mt-1">{x.note}</p>
                                <p className="text-xs text-neon mt-2">{x.good}</p>
                            </a>
                        ))}
                    </div>
                    <div className="mt-5 p-4 border border-gold/30 bg-gold/5 flex items-start gap-3">
                        <AlertCircle className="w-4 h-4 text-gold mt-0.5 shrink-0" />
                        <p className="text-xs text-ink-dim leading-relaxed">
                            <strong className="text-gold">ID verification (KYC):</strong> All regulated exchanges
                            require government ID to comply with anti-money-laundering rules. The check usually
                            takes 5 to 30 minutes. <strong className="text-ink">Helixora never sees your ID</strong>.
                            That stays between you and the exchange.
                        </p>
                    </div>
                </section>

                {/* Step 2 */}
                <section className="mt-16">
                    <p className="overline text-gold mb-3">/ 03 &middot; BUY USDC</p>
                    <h2 className="font-display text-3xl font-bold text-ink leading-tight mb-4 flex items-center gap-3">
                        <Coins className="w-7 h-7 text-gold" /> Buy USDC on Solana, not Bitcoin.
                    </h2>
                    <p className="text-base text-ink-dim leading-relaxed">
                        <strong className="text-ink">USDC</strong> is a stablecoin pinned 1-for-1 to the
                        US dollar. $300 of USDC will always equal $300, so the amount you send equals the amount we receive.
                        No surprises from price swings.
                    </p>
                    <p className="text-base text-ink-dim leading-relaxed mt-3">
                        On your exchange, hit <strong className="text-ink">Buy</strong>, pick <strong className="text-ink">USDC</strong>, and pay with a debit card,
                        bank transfer, or whatever&apos;s available. Most exchanges process card buys in seconds. When you
                        get to the <strong className="text-ink">Send</strong> step, choose the <strong className="text-neon">Solana</strong> network.
                        It&apos;s the cheapest (typically under $0.01 in fees vs. $5 to $30 on Ethereum).
                    </p>
                    <div className="mt-5 p-4 border border-ink-strong bg-ink-surface">
                        <p className="overline text-neon mb-2 text-[10px]">PREFER ANOTHER COIN?</p>
                        <p className="text-xs text-ink-dim leading-relaxed">
                            We also accept <strong className="text-ink">USDT (TRC20)</strong>, <strong className="text-ink">BTC</strong>,
                            <strong className="text-ink"> ETH (ERC-20)</strong>, and <strong className="text-ink">XRP</strong>.
                            Pick whichever you already hold. Non-stablecoins (BTC/ETH/XRP) can fluctuate slightly while the
                            transaction confirms. USDC and USDT avoid that entirely.
                        </p>
                    </div>
                </section>

                {/* Step 3 */}
                <section className="mt-16">
                    <p className="overline text-gold mb-3">/ 04 &middot; PAY YOUR ORDER</p>
                    <h2 className="font-display text-3xl font-bold text-ink leading-tight mb-4 flex items-center gap-3">
                        <CheckCircle2 className="w-7 h-7 text-neon" /> Send the payment. 30 seconds.
                    </h2>
                    <ol className="space-y-4 text-base text-ink-dim leading-relaxed list-decimal pl-5">
                        <li>
                            On <strong className="text-ink">helixorapeptides.com</strong>, add your products to cart and proceed to checkout.
                            Fill in shipping &amp; click <strong className="text-neon">PAY WITH CRYPTO</strong>.
                        </li>
                        <li>
                            You&apos;ll land on a secure <strong className="text-ink">NowPayments</strong> page showing:
                            <ul className="mt-2 space-y-1 text-sm list-disc pl-5">
                                <li>The exact amount of crypto to send (e.g. <span className="font-mono text-ink">300.00 USDC</span>)</li>
                                <li>A long alphanumeric <strong className="text-ink">wallet address</strong></li>
                                <li>The <strong className="text-neon">network</strong> to send on (e.g. <strong className="text-ink">Solana</strong> for USDC)</li>
                                <li>A QR code (handy for mobile)</li>
                            </ul>
                        </li>
                        <li>
                            In your exchange app (Coinbase, Kraken, etc.) tap <strong className="text-ink">Send</strong> on your USDC balance.
                            Paste the wallet address. <strong className="text-gold">Confirm the network matches what the invoice shows.</strong>
                            Enter the exact amount. <strong className="text-gold">Double-check the address</strong>. Send.
                        </li>
                        <li>
                            Come back to our success page. Once the network confirms (1 to 5 min for USDC/USDT/XRP, up to 60 min for BTC),
                            your order is automatically marked paid and you&apos;ll get an email confirmation.
                        </li>
                    </ol>
                    <div className="mt-5 p-4 border border-red-500/40 bg-red-500/5 flex items-start gap-3">
                        <AlertCircle className="w-4 h-4 text-red-400 mt-0.5 shrink-0" />
                        <div className="text-xs text-ink-dim leading-relaxed space-y-2">
                            <p><strong className="text-red-300">Triple-check the wallet address.</strong> Crypto transfers are irreversible. Always copy/paste. Never type by hand.</p>
                            <p><strong className="text-red-300">Send the EXACT amount.</strong> If you underpay by even $1, the payment won&apos;t auto-confirm and you&apos;ll need to email us.</p>
                            <p><strong className="text-red-300">Send on the MATCHING network.</strong> USDC on Solana &ne; USDC on Ethereum. USDT on TRC20 &ne; USDT on ERC-20. Most exchanges ask which network. Pick the one the invoice shows.</p>
                        </div>
                    </div>
                </section>

                {/* What if */}
                <section className="mt-16">
                    <p className="overline text-gold mb-3">/ 05 &middot; FAQ</p>
                    <h2 className="font-display text-3xl font-bold text-ink leading-tight mb-6 flex items-center gap-3">
                        <Clock className="w-7 h-7 text-gold" /> What if something goes wrong?
                    </h2>
                    <div className="space-y-5 text-sm text-ink-dim leading-relaxed">
                        <div>
                            <p className="font-display text-base font-bold text-ink mb-1">Q. I sent the wrong amount.</p>
                            <p>Email <a href="mailto:orders@helixorapeptides.com" className="text-neon hover:underline">orders@helixorapeptides.com</a> with your order number and the transaction hash from your exchange (it&apos;s on the &laquo;Sent&raquo; receipt). We&apos;ll either credit the difference or refund the overpayment to your wallet.</p>
                        </div>
                        <div>
                            <p className="font-display text-base font-bold text-ink mb-1">Q. I sent on the wrong network.</p>
                            <p>Don&apos;t panic. Email us right away with the transaction hash. If both networks are supported by your exchange wallet we can usually trace and credit it. If not, the funds may be unrecoverable, so always double-check the network on the invoice.</p>
                        </div>
                        <div>
                            <p className="font-display text-base font-bold text-ink mb-1">Q. My payment isn&apos;t confirming.</p>
                            <p>USDC on Solana confirms in 1 to 2 minutes. USDT on TRC20 in 2 to 5 minutes. XRP in seconds. Bitcoin can take 10 to 60 minutes. ETH 1 to 5 minutes. If it&apos;s been over 60 min, send us the transaction hash and we&apos;ll trace it manually.</p>
                        </div>
                        <div>
                            <p className="font-display text-base font-bold text-ink mb-1">Q. Can I use a non-custodial wallet (Phantom, MetaMask, Trust Wallet)?</p>
                            <p>Yes. Any wallet that can send the coin on the matching network works. Phantom is the standard for Solana (USDC), MetaMask for Ethereum (ETH), Trust Wallet does both. You don&apos;t need to use an exchange. That&apos;s just the easiest path for beginners.</p>
                        </div>
                        <div>
                            <p className="font-display text-base font-bold text-ink mb-1">Q. Is this anonymous?</p>
                            <p>From our side, we only see the wallet address and amount. Not your name, ID, or bank. The exchange you bought USDC from knows your identity (KYC), but they don&apos;t know what you&apos;re spending it on.</p>
                        </div>
                        <div>
                            <p className="font-display text-base font-bold text-ink mb-1">Q. Refunds?</p>
                            <p>If we need to refund (out of stock, label issue, etc.) we send USDC back to the same wallet address you paid from on the same network. Refunds typically settle within 1 business day.</p>
                        </div>
                    </div>
                </section>

                {/* Final CTA */}
                <div className="mt-16 p-8 border border-neon bg-ink-surface text-center" data-testid="cta-block">
                    <ShieldCheck className="w-10 h-10 text-neon mx-auto mb-3" strokeWidth={1.3} />
                    <h3 className="font-display text-3xl font-bold text-ink leading-tight">Ready when you are.</h3>
                    <p className="text-sm text-ink-dim mt-3 max-w-md mx-auto">
                        Still have questions? Email <a href="mailto:orders@helixorapeptides.com" className="text-neon hover:underline">orders@helixorapeptides.com</a>. We usually reply within a few hours.
                    </p>
                    <Link to="/shop"
                        className="inline-flex items-center gap-2 mt-6 bg-neon hover:bg-neon-glow text-black px-8 py-4 font-bold transition-colors"
                        data-testid="back-to-shop-cta">
                        BROWSE PRODUCTS <ArrowUpRight className="w-4 h-4" />
                    </Link>
                </div>
            </div>
        </div>
    );
}
